import { SupabaseClient } from '@supabase/supabase-js';
import { TransformOptions } from '@habity/types/storage';

type AvatarVariants = 'thumbnail' | 'list' | 'homeProfile' | 'brokerProfile';

/**
 * Cloudflare flexible variant transformations for avatars
 */
const AVATAR_FLEXIBLE_VARIANT_MAP: Record<AvatarVariants | 'default', string> =
  {
    thumbnail: 'w=50,fit=contain,quality=85,format=auto',
    list: 'h=340,fit=contain,quality=85,format=auto',
    homeProfile: 'h=500,fit=contain,quality=85,format=auto',
    brokerProfile: 'h=864,fit=contain,quality=85,format=auto',
    default: 'w=250,fit=contain,quality=85,format=auto',
  };

export const getAvatarTransformOptions = (
  variant?: AvatarVariants
): TransformOptions => {
  switch (variant) {
    case 'thumbnail':
      return {
        width: 50,
        resize: 'contain',
        quality: 85,
      };
    case 'list':
      return {
        height: 340,
        resize: 'contain',
        quality: 85,
      };
    case 'homeProfile':
      return {
        height: 500,
        resize: 'contain',
        quality: 85,
      };
    case 'brokerProfile':
      return {
        height: 864,
        resize: 'contain',
        quality: 85,
      };
    default:
      return {
        width: 250,
        resize: 'contain',
        quality: 85,
      };
  }
};

export const getAvatarUrl = (
  supabase: SupabaseClient,
  bucket: string,
  avatarId: number,
  extension = '',
  transform = getAvatarTransformOptions(),
  email?: string,
  cloudflareImageId?: string | null
) => {
  if (!cloudflareImageId) {
    return `https://imagecdn.erikolsson.se/o365/${email || ''}/hemsida`;
  }

  const accountHash = process.env.NEXT_PUBLIC_CLOUDFLARE_ACCOUNT_HASH;
  if (!accountHash) {
    console.warn('NEXT_PUBLIC_CLOUDFLARE_ACCOUNT_HASH not set');
    return `https://imagecdn.erikolsson.se/o365/${email || ''}/hemsida`;
  }

  // Map transform to variant
  const getVariantFromTransform = (t: TransformOptions): string => {
    const dimensionToVariant: Record<number, string> = {
      50: AVATAR_FLEXIBLE_VARIANT_MAP.thumbnail,
      340: AVATAR_FLEXIBLE_VARIANT_MAP.list,
      500: AVATAR_FLEXIBLE_VARIANT_MAP.homeProfile,
      864: AVATAR_FLEXIBLE_VARIANT_MAP.brokerProfile,
    };

    const dimension = t.width || t.height || 250;
    return dimensionToVariant[dimension] || AVATAR_FLEXIBLE_VARIANT_MAP.default;
  };

  const variant = getVariantFromTransform(transform);
  return `https://imagedelivery.net/${accountHash}/${cloudflareImageId}/${variant}`;
};

export const getUserAvatarUrl = (
  supabase: SupabaseClient,
  bucket: string,
  userId: number,
  extension: string,
  transform = getAvatarTransformOptions()
): string => {
  if (!userId || !extension) {
    return `https://imagecdn.avatarapi.changeme/`;
  }

  // SUPABASE: Call backend fn instead of sb directly
  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(`${userId}/${userId}.${extension}`, { transform });

  return data?.publicUrl || `https://imagecdn.avatarapi.changeme/`;
};

export const getDeveloperLogoUrl = (
  supabase: SupabaseClient,
  developerId: number,
  extension: string,
  transform = getAvatarTransformOptions()
): string => {
  if (!developerId || !extension) {
    return '';
  }

  // SUPABASE: Call backend fn instead of sb directly
  const { data } = supabase.storage
    .from('developers')
    .getPublicUrl(`${developerId}/${developerId}.${extension}`, { transform });

  return data?.publicUrl || '';
};
